import { Frequency } from '@cf/shared';
/**
 * Plan-completeness check ("nothing takes anything away").
 *
 * A projection whose plan has no outgoings can only compound upward — which
 * is usually a fact-find gap, not a realistic outlook. This module inspects
 * the PLAN INPUTS (never the projected numbers) and returns warnings that
 * ride the ordinary projection `warnings` array, so the builder preview, the
 * API preview and persisted runs all surface them. The client-facing report
 * filters these codes out (they are prompts for the adviser's fact-find, not
 * notes for the client — see ReportDocument).
 *
 * Pure and deterministic: derived only from the inputs, the projection
 * window and (optionally) the already-expanded planned-event spending — no
 * clock, no engine state. Every message is an illustrative prompt about what
 * has not been captured yet; none of it is advice, a recommendation or an
 * optimisation, and the wording stays person-neutral and non-directive.
 */
/** Planned-event kinds that expand into living-cost spending. */
const SPENDING_EVENT_KINDS = new Set(['schoolFees', 'university', 'care']);
function isOutflowEvent(event) {
    if (SPENDING_EVENT_KINDS.has(event.kind))
        return true;
    if (event.kind === 'propertyPurchase')
        return true;
    if (event.kind === 'recurring')
        return (event.direction ?? 'outflow') === 'outflow';
    return false; // property/business sales and career breaks are not outgoings
}
function isSpendingEvent(event) {
    if (SPENDING_EVENT_KINDS.has(event.kind))
        return true;
    if (event.kind === 'recurring')
        return (event.direction ?? 'outflow') === 'outflow';
    return false;
}
/**
 * The year a spending item stops, or null when it runs to the horizon. A
 * one-off with no explicit end lands only in its start year — treating it as
 * open-ended would wrongly silence the ends-early prompt.
 */
function effectiveEndYear(item, startYear) {
    if (item.endYear !== null)
        return item.endYear;
    if (item.frequency === Frequency.Once)
        return item.startYear ?? startYear;
    return null;
}
const NOT_ADVICE = 'This is a note about missing information in the plan, not advice.';
/**
 * Inspect a plan for missing outgoings. `lastYear` is the final projected
 * tax year (start-year form). `expandedSpending` is the planned-event
 * spending already expanded by the engine (per-year items with concrete
 * years); when it is supplied the ends-early prompt can judge event windows
 * precisely, and when it is not, plans whose spending comes only from
 * planned events are given the benefit of the doubt.
 */
export function checkPlanCompleteness(input, startYear, lastYear, expandedSpending = []) {
    const warnings = [];
    const plannedEvents = input.plannedEvents ?? [];
    const lifeEvents = input.lifeEvents ?? [];
    const rawItems = input.spending.filter((s) => s.amount > 0);
    const effectiveItems = [...rawItems, ...expandedSpending.filter((s) => s.amount > 0)];
    const hasSpending = effectiveItems.length > 0 || plannedEvents.some(isSpendingEvent);
    const hasLiabilities = input.liabilities.some((l) => l.balance > 0);
    const hasOutflowEvents = lifeEvents.some((e) => e.direction === 'outflow' && e.amount > 0) ||
        plannedEvents.some(isOutflowEvent);
    const hasIncome = input.incomeStreams.some((s) => s.amount > 0);
    const hasAssets = input.assets.some((a) => a.currentValue > 0 || a.contribution > 0);
    // An entirely empty plan projects nothing worth warning about.
    if (!hasIncome && !hasAssets)
        return warnings;
    if (!hasSpending && !hasLiabilities && !hasOutflowEvents) {
        // The headline covers the missing spending too — emitting the spending
        // prompt as well would say the same thing twice.
        warnings.push({
            code: 'plan-no-outflows',
            message: 'No outgoings of any kind are captured in this plan — no spending, no loan or mortgage payments, and no outgoing life events. ' +
                'Projected wealth can only grow, because nothing in the plan reduces it, so the projection is likely to look unrealistically high until outgoings are recorded. ' +
                NOT_ADVICE,
            year: null,
            itemLabel: null,
        });
        return warnings;
    }
    if (!hasSpending) {
        warnings.push({
            code: 'plan-no-spending',
            message: 'No living costs have been captured — projected wealth will be unrealistically high until essential and lifestyle spending is added to the plan. ' +
                NOT_ADVICE,
            year: null,
            itemLabel: null,
        });
        return warnings;
    }
    // Categorised spending with nothing marked essential. Deliberately inert
    // when NO item carries a category (imported/legacy plans): guessing would
    // nag every plan that simply does not use the categories.
    const categorised = rawItems.filter((s) => s.category !== undefined);
    if (categorised.length > 0 && !categorised.some((s) => s.category === 'essential')) {
        warnings.push({
            code: 'plan-no-essential-spending',
            message: 'Spending is captured but none of it is categorised as essential living costs — housing, utilities and food may not have been recorded yet. ' +
                NOT_ADVICE,
            year: null,
            itemLabel: null,
        });
    }
    // Every captured spending item stops before the projection does. Judged
    // over the effective items (raw + expanded events) so a plan whose school
    // fees end decades early is flagged and an ongoing care plan is not. When
    // the expansion was not supplied, spending-kind planned events suppress
    // the prompt (their windows cannot be resolved here).
    const unresolvedEvents = expandedSpending.length === 0 && plannedEvents.some(isSpendingEvent);
    if (effectiveItems.length > 0 && !unresolvedEvents) {
        const ends = effectiveItems.map((s) => effectiveEndYear(s, startYear));
        if (!ends.includes(null)) {
            const maxEnd = Math.max(startYear, ...ends);
            if (maxEnd < lastYear) {
                const gap = lastYear - maxEnd;
                warnings.push({
                    code: 'plan-spending-ends-early',
                    message: `All captured spending ends in ${maxEnd}, ${gap} ${gap === 1 ? 'year' : 'years'} before the projection ends in ${lastYear} — ` +
                        `later-life costs (such as care) may be missing. ${NOT_ADVICE}`,
                    year: maxEnd,
                    itemLabel: null,
                });
            }
        }
    }
    return warnings;
}
//# sourceMappingURL=plan-completeness.js.map